import { useState, useEffect, useCallback } from 'react';
import { Card, Stack, Container, StackSeparator, Center, Spinner } from '@chakra-ui/react';
import { useParams, useLoaderData } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { ContentType, BlockType, FieldOption, StaticField } from '@/lib/types';
import FormContent from './form-content';
import { STATIC_FIELDS } from '@/lib/constants';

interface ContentTypeField {
  id: number;
  referenceID: string;
  field_id: string;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  field_options: FieldOption[];
  workspace_id: number;
  content_type_id: number;
  sort_order: number;
  properties: any;
}

const processSelectionField = (field: ContentTypeField): ContentTypeField => {
  const fieldCopy = { ...field };

  if (field.field_type === BlockType.Selection) {
    fieldCopy.properties = { ...fieldCopy.properties, options: [] };
    const sortedOptions = [...field.field_options].sort(
      (a: FieldOption, b: FieldOption) => a.sort_order - b.sort_order,
    );

    fieldCopy.properties.options = sortedOptions.map((item: FieldOption) => ({
      label: item.option_label,
      value: item.option_value,
    }));
  }

  return fieldCopy;
};

const processStaticField = (field: ContentTypeField): ContentTypeField => {
  const fieldCopy = { ...field };

  if (field.field_unique_name && STATIC_FIELDS.includes(field.field_unique_name as StaticField)) {
    fieldCopy.referenceID = field.field_unique_name;
  }

  return fieldCopy;
};

export default function CreateContent() {
  const fields = useLoaderData() as ContentTypeField[];
  const [rootBlocks, setRootBlocks] = useState<ContentTypeField[]>([]);
  const [contentType, setContentType] = useState<ContentType>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { contentTypeId } = useParams<{
    contentTypeId: string;
  }>();

  const fetchContentType = useCallback(async () => {
    if (!contentTypeId) return;

    setIsLoading(true);

    try {
      const response = await api.get(`content-types/${contentTypeId}`);
      const fetchedContentType = response.data.data;
      setContentType(fetchedContentType);

      const processedFields = fields.map((field) => {
        let processedField = processSelectionField(field);
        processedField = processStaticField(processedField);

        return processedField;
      });

      const filteredFields = processedFields.filter((field) => {
        return !['s_created_date', 's_is_public'].includes(field.field_unique_name);
      });

      const sortedFields = [...filteredFields].sort((a, b) => a.sort_order - b.sort_order);

      const staticFieldsList: ContentTypeField[] = [];
      const sortableFieldsList: ContentTypeField[] = [];

      sortedFields.forEach((field: ContentTypeField) => {
        if (STATIC_FIELDS.includes(field.field_unique_name as StaticField)) {
          staticFieldsList.push(field);
        } else {
          sortableFieldsList.push(field);
        }
      });

      setRootBlocks([...staticFieldsList, ...sortableFieldsList]);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load content type',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [contentTypeId, fields]);

  useEffect(() => {
    fetchContentType();
  }, [fetchContentType]);

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header textAlign={'center'}>
        <Card.Title fontSize='3xl'>Create {contentType?.name || 'Content'}</Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <Center h='300px'>
            <Spinner size='xl' />
          </Center>
        )}
        <Container maxW={'3xl'}>
          <Stack gap={4} separator={<StackSeparator />}>
            <FormContent rootBlocks={rootBlocks} responses={{}} />
          </Stack>
        </Container>
      </Card.Body>
    </Card.Root>
  );
}
